import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable } from 'rxjs';
import { AddH265CamerasRequest, CustomCameraNameRequestModel, H265Camera, ILiveViewModelData, IWowzaResponse, PtzInfo, SitesAndCamerasModel, SiteWithCameraCount, WowzaResponse } from '../models/live-view.model';
import { MainHttpService } from './main-http.service';
import { AppLoggingService } from 'src/app/_shared/core/services/app-logging.service';
import { DefaultLayoutOption } from '../models/user-setting.model';

@Injectable({
  providedIn: 'root'
})
export class LiveViewService {
  // public fields
  isLoading$: Observable<boolean>;
  isLoadingSubject: BehaviorSubject<boolean>;
  isCameraConnecting$: Observable<boolean>;
  isCameraConnectingSubject: BehaviorSubject<boolean>;
  isPtz$: Observable<boolean>;
  isPtzSubject: BehaviorSubject<boolean>;

  constructor(
    private readonly mainHttpService: MainHttpService,
    private readonly appLoggingService: AppLoggingService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.isCameraConnectingSubject = new BehaviorSubject<boolean>(false);
    this.isCameraConnecting$ = this.isCameraConnectingSubject.asObservable();
    this.isPtzSubject = new BehaviorSubject<boolean>(false);
    this.isPtz$ = this.isPtzSubject.asObservable();
  }

  getAllSiteDetailsAndCameras(): Observable<SitesAndCamerasModel[]> {
    this.isLoadingSubject.next(true);
    return this.mainHttpService.getAllSiteDetailsAndCameras().pipe(
      map((sites: ILiveViewModelData) => {
        return sites.result.map(r => ({
          siteId: r.siteId,
          siteName: r.siteName,
          siteAddress: r.siteAddress,
          cameraCount: r.cameraCount,
          cameras: r.cameras
        }));
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  connectStreamByName(streamFileName: string, ipAddress :string, wowzaApplicationName: string = "connect"): Observable<WowzaResponse> {
    const startDate = new Date();
    this.isCameraConnectingSubject.next(true);
    return this.mainHttpService.connectWowzaStreambyName(streamFileName,ipAddress,wowzaApplicationName).pipe(
      map((wowzaResponse: IWowzaResponse) => {
        wowzaResponse.result.startDate=startDate;
        return wowzaResponse.result;
      }),
      finalize(() => this.isCameraConnectingSubject.next(false))
    );
  }

  convertDatetimeToString(date){
    let endDay = date.getDate();
    let endMonth = date.getMonth() + 1; //months are zero based
    let endYear = date.getFullYear();
    let endHH = date.getHours();
    let endMM = date.getMinutes();
    let endSS = date.getSeconds();
    return `${endMonth}/${endDay}/${endYear} ${endHH}:${endMM}:${endSS}`;
  }

  ptz(ptzInfo: PtzInfo): Observable<void> {
    this.isPtzSubject.next(true);
    return this.mainHttpService.ptz(ptzInfo).pipe(
      map((result: any) => {
        return result
      }),
      finalize(() => this.isPtzSubject.next(false))
    );
  }

  disconnectStreamByName(streamFileName: string, ipAddress :string, wowzaApplicationName: string = "connect"){
    this.mainHttpService.disconnectWowzaStreambyName(streamFileName, ipAddress, wowzaApplicationName);
  }

  disconnectStreamsByTenant(tenantId: number) {
    this.mainHttpService.disconnectWowzaStreamsByTenant(tenantId);
  }

  updateCustomCameraName(camera: CustomCameraNameRequestModel): Observable<any> {
    return this.mainHttpService.updateCustomCameraName(camera).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  getAllSitesList(): Observable<any> {
    return this.mainHttpService.getAllSitesList().pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  getCamerasBySiteId(siteId: number): Observable<any> {
    return this.mainHttpService.getCamerasBySiteId(siteId).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  getNvrBySiteId(siteId: number): Observable<any> {
    return this.mainHttpService.getNvrBySiteId(siteId).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  getFavoriteViewCameraBySiteId(siteId: number): Observable<any> {
    return this.mainHttpService.getFavoriteViewCameraBySiteId(siteId).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  getSiteWithCameraCount(): Observable<SiteWithCameraCount[]> {
    return this.mainHttpService.getSiteWithCameraCount().pipe(
      map((response: SiteWithCameraCount[]) => {
        return response;
      })
    );
  }

  getUpdatedLayout(userId: number) {
    return this.mainHttpService.getLayoutbyId(userId).pipe(
      map((response: DefaultLayoutOption) => {
        return response;
      })
    )
  }
  
  setUpdatedLayout(body: DefaultLayoutOption): Observable<boolean> {
    return this.mainHttpService.setLayoutbyId(body).pipe(
      map((response: boolean) => {
        return response;
      })
    ) 
  }

  getH265CameraList(): Observable<H265Camera[]> {
    return this.mainHttpService.getH265CameraList().pipe(
      map((response: H265Camera[]) => {
        return response;
      })
    );
  }

  addH265CamerasList(siteId: number, request: AddH265CamerasRequest): Observable<H265Camera[]> {
    return this.mainHttpService.addH265CamerasList(siteId, request).pipe(
      map((response: H265Camera[]) => {
        return response;
      })
    )
  }

}
